import { LayoutProps } from '@/components/layout/Layout'
import RumPageTracker from '@/components/utils/RumPageTracker'
import { useAuth } from '@/context/AuthContext'
import { useClient } from '@/context/ClientContext'
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import { QueryClient } from '@tanstack/react-query'
import {
  ErrorComponentProps,
  Link,
  Outlet,
  createRootRouteWithContext,
  useRouter,
} from '@tanstack/react-router'
import { useEffect } from 'react'

import { Card, CardContent, CardHeader, CardTitle } from '@droidmap/frontend-ui'
import { Button } from '@droidmap/frontend-ui'
import { DroidMapMetadata } from '@droidmap/shared-service-contract'

export interface RouterContext {
  auth: ReturnType<typeof useAuth>
  client: ReturnType<typeof useClient>
  queryClient: QueryClient
}

export type PrecedingCrumb = {
  label: string
  to: string
  params?: Record<string, string>
}

export type RouteLoaderData = {
  crumb?: string
  /**
   * Synthetic breadcrumb items injected immediately before this route's
   * own crumb. Useful when a detail route lives at a different parent
   * in the file tree (e.g. asset-set detail) than its conceptual
   * navigation parent (the asset-sets list).
   */
  precedingCrumbs?: PrecedingCrumb[]
  assetId?: string
  metadata?: DroidMapMetadata
  layout?: LayoutProps
}

// Error component that redirects to 404 for notFound errors
function RootErrorComponent({ error }: ErrorComponentProps) {
  const router = useRouter()

  useEffect(() => {
    if (error?.message === 'Not Found' || error?.name === 'NotFoundError') {
      router.navigate({ to: '/404' })
    }
  }, [error, router])
  // Show a generic error message for all other errors
  if (error?.message !== 'Not Found' && error?.name !== 'NotFoundError') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 h-12 w-12 text-danger">
              <ExclamationTriangleIcon />
            </div>
            <CardTitle className="text-2xl">
              {error?.message || 'Something went wrong'}
            </CardTitle>
          </CardHeader>
          <CardContent className="text-center">
            <div className="space-y-3">
              <Link to="/">
                <Button size="wide" variant="default">
                  Go to Home
                </Button>
              </Link>
              <Button
                size="wide"
                variant="outline"
                onClick={() => window.history.back()}
              >
                Go Back
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  return null
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: () => (
    <>
      <RumPageTracker />
      <Outlet />
    </>
  ),
  errorComponent: RootErrorComponent,
})
